import DeleteIcon from '@mui/icons-material/Delete'
import DownloadIcon from '@mui/icons-material/Download'
import PlagiarismOutlinedIcon from '@mui/icons-material/PlagiarismOutlined'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { LINK_SOURCE_LABELS, type DocumentDto, type TabuExtract } from '@lawyer/shared'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { ApiError, downloadFile } from '../../lib/api'
import { api } from '../../lib/api'
import { formatBytes, formatDateTime } from '../../lib/format'
import { ConfirmDialog } from '../common/ConfirmDialog'
import { EmptyState, ErrorAlert } from '../common/Feedback'
import { TabuExtractDialog } from './TabuExtractDialog'

const isPdf = (d: DocumentDto) =>
  d.mimeType?.includes('pdf') || d.name.toLowerCase().endsWith('.pdf')

type Props = {
  documents: DocumentDto[]
  emptyText?: string
  /** Query keys to refresh after a delete. */
  onChanged: () => void
  showSource?: boolean
}

export function DocumentList({ documents, emptyText = 'אין מסמכים', onChanged, showSource = true }: Props) {
  const queryClient = useQueryClient()
  const [pendingDelete, setPendingDelete] = useState<DocumentDto | null>(null)
  const [downloadError, setDownloadError] = useState<unknown>(null)
  const [tabuView, setTabuView] = useState<{ name: string; extract: TabuExtract } | null>(null)

  // Extraction runs only on this explicit click — never on upload.
  const extractTabu = useMutation({
    mutationFn: (document: DocumentDto) =>
      api.post<TabuExtract>(`/documents/${document.id}/tabu-extract`),
    onSuccess: (extract, document) => {
      setTabuView({ name: document.name, extract })
      onChanged()
      void queryClient.invalidateQueries({ queryKey: ['documents'] })
    },
  })

  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/documents/${id}`),
    onSuccess: () => {
      setPendingDelete(null)
      onChanged()
      void queryClient.invalidateQueries({ queryKey: ['documents'] })
    },
  })

  const download = async (document: DocumentDto) => {
    setDownloadError(null)
    try {
      await downloadFile(`/documents/${document.id}/download`, document.name)
    } catch (err) {
      setDownloadError(err instanceof ApiError ? err : new Error('ההורדה נכשלה'))
    }
  }

  if (documents.length === 0) return <EmptyState title={emptyText} />

  return (
    <>
      {downloadError ? <ErrorAlert error={downloadError} /> : null}
      {remove.error ? <ErrorAlert error={remove.error} /> : null}
      {extractTabu.error ? <ErrorAlert error={extractTabu.error} /> : null}

      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>שם</TableCell>
              <TableCell>סוג</TableCell>
              <TableCell>תיאור</TableCell>
              <TableCell>גודל</TableCell>
              <TableCell>הועלה</TableCell>
              <TableCell align="left" />
            </TableRow>
          </TableHead>
          <TableBody>
            {documents.map((document) => (
              <TableRow key={document.id} hover>
                <TableCell>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2">{document.name}</Typography>
                    {showSource && document.source !== 'case' ? (
                      <Chip size="small" variant="outlined" label={LINK_SOURCE_LABELS[document.source]} />
                    ) : null}
                  </Stack>
                </TableCell>
                <TableCell>{document.documentTypeName ?? '—'}</TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {document.description || '—'}
                  </Typography>
                </TableCell>
                <TableCell>{formatBytes(document.sizeBytes)}</TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">
                    {document.uploadedByName} · {formatDateTime(document.uploadedAt)}
                  </Typography>
                </TableCell>
                <TableCell align="left">
                  {document.tabuExtract ? (
                    <Chip
                      size="small"
                      color="secondary"
                      variant="outlined"
                      label="נתוני נסח"
                      onClick={() =>
                        document.tabuExtract &&
                        setTabuView({ name: document.name, extract: document.tabuExtract })
                      }
                      sx={{ me: 0.5 }}
                    />
                  ) : isPdf(document) ? (
                    <Tooltip title="חילוץ נתוני נסח טאבו">
                      <span>
                        <IconButton
                          size="small"
                          color="secondary"
                          disabled={extractTabu.isPending}
                          onClick={() => extractTabu.mutate(document)}
                        >
                          {extractTabu.isPending && extractTabu.variables?.id === document.id ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : (
                            <PlagiarismOutlinedIcon fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  ) : null}
                  <Tooltip title="הורדה">
                    <IconButton size="small" onClick={() => void download(document)}>
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="מחיקה">
                    <IconButton size="small" onClick={() => setPendingDelete(document)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {tabuView ? (
        <TabuExtractDialog
          open
          documentName={tabuView.name}
          extract={tabuView.extract}
          onClose={() => setTabuView(null)}
        />
      ) : null}

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        title="מחיקת מסמך"
        message={`הקובץ "${pendingDelete?.name ?? ''}" יימחק מהאחסון ומהמערכת. להמשיך?`}
        confirmLabel="מחיקה"
        busy={remove.isPending}
        onConfirm={() => pendingDelete && remove.mutate(pendingDelete.id)}
        onClose={() => setPendingDelete(null)}
      />
    </>
  )
}
